import { N, round, ceil, COPPER, gasDischarge } from './helpers.mjs';

export const PLUMBERS = [
  // ====================== PLUMBERS ======================
  {
    slug: 'heat-loss',
    trade: 'plumbers',
    name: 'Heat Loss (Boiler Sizing)',
    blurb: 'Quick whole-property heat loss estimate from floor area and insulation standard, with a suggested boiler output. Guidance only — use a full room-by-room heat loss for design.',
    inputs: [
      { key: 'area', label: 'Total floor area', unit: 'm²', placeholder: '90' },
      { key: 'standard', label: 'Property / insulation', type: 'select', default: '70',
        options: [
          { value: '40', label: 'New build / well insulated (40 W/m²)' },
          { value: '55', label: '2000s, good insulation (55 W/m²)' },
          { value: '70', label: '1990s, average (70 W/m²)' },
          { value: '90', label: 'Older, some insulation (90 W/m²)' },
          { value: '110', label: 'Solid wall / poor (110 W/m²)' },
        ] },
      { key: 'dhw', label: 'Add hot-water allowance?', type: 'select', default: '3',
        options: [{ value: '0', label: 'No (heat only)' }, { value: '3', label: '+3 kW (cylinder)' }, { value: '0', label: 'Combi (instant DHW)' }] },
    ],
    compute: (v) => {
      const area = N(v.area);
      if (area <= 0) return [{ label: 'Enter the floor area', value: 0, unit: '' }];
      const loss = (area * parseFloat(v.standard)) / 1000;
      const boiler = loss + parseFloat(v.dhw || 0);
      return [
        { label: 'Estimated heat loss', value: round(loss, 1), unit: 'kW' },
        { label: 'Suggested boiler output', value: round(boiler, 1), unit: 'kW' },
      ];
    },
  },
  {
    slug: 'underfloor-heating',
    trade: 'plumbers',
    name: 'Underfloor Heating Pipe',
    blurb: 'Pipe length and number of manifold loops for a wet underfloor heating zone, from area and pipe spacing.',
    inputs: [
      { key: 'area', label: 'Floor area', unit: 'm²', placeholder: '20' },
      { key: 'spacing', label: 'Pipe spacing', type: 'select', default: '200',
        options: [{ value: '100', label: '100mm (high output)' }, { value: '150', label: '150mm' }, { value: '200', label: '200mm (standard)' }, { value: '300', label: '300mm (low output)' }] },
      { key: 'maxLoop', label: 'Max loop length', unit: 'm', placeholder: '100', default: 100 },
    ],
    compute: (v) => {
      const area = N(v.area);
      if (area <= 0) return [{ label: 'Enter the floor area', value: 0, unit: '' }];
      const pipe = (area / (parseFloat(v.spacing) / 1000)) * 1.1; // +10% bends/tails
      const loops = ceil(pipe / (N(v.maxLoop) || 100));
      return [
        { label: 'Pipe required', value: round(pipe, 1), unit: 'm' },
        { label: 'Manifold loops / ports', value: loops, unit: '' },
        { label: 'Length per loop (≈)', value: round(pipe / loops, 1), unit: 'm' },
      ];
    },
  },
  {
    slug: 'expansion-vessel',
    trade: 'plumbers',
    name: 'Expansion Vessel Sizing',
    blurb: 'Minimum expansion vessel size for a sealed heating system, from system water content, cold-fill and safety-valve pressures.',
    inputs: [
      { key: 'sysVol', label: 'System water content', unit: 'L', placeholder: '100' },
      { key: 'cold', label: 'Cold-fill (charge) pressure', unit: 'bar', placeholder: '1.0', default: 1.0 },
      { key: 'relief', label: 'Safety-valve pressure', unit: 'bar', placeholder: '3.0', default: 3.0 },
    ],
    compute: (v) => {
      const sys = N(v.sysVol);
      if (sys <= 0) return [{ label: 'Enter the system volume', value: 0, unit: '' }];
      const e = 0.0359; // expansion 10→90°C
      const acceptance = 1 - ((N(v.cold) + 1) / (N(v.relief) + 1));
      const vessel = acceptance > 0 ? (sys * e) / acceptance : 0;
      const std = [8, 12, 18, 24, 35, 50].find((s) => s >= vessel) || '>50';
      return [
        { label: 'Minimum vessel volume', value: round(vessel, 1), unit: 'L' },
        { label: 'Suggested standard vessel', value: std, unit: 'L' },
      ];
    },
  },
  {
    slug: 'pipe-sizing',
    trade: 'plumbers',
    name: 'Pipe Sizing (by Flow)',
    blurb: 'Minimum bore and copper pipe size for a required flow rate, using a target water velocity (≈2 m/s).',
    inputs: [
      { key: 'flow', label: 'Flow rate', unit: 'L/min', placeholder: '20' },
      { key: 'velocity', label: 'Max velocity', unit: 'm/s', placeholder: '2.0', default: 2.0 },
    ],
    compute: (v) => {
      const flow = N(v.flow); const vel = N(v.velocity) || 2.0;
      if (flow <= 0) return [{ label: 'Enter a flow rate', value: 0, unit: '' }];
      const q = flow / 60000; // m³/s
      const area = q / vel;
      const dreq = Math.sqrt((4 * area) / Math.PI) * 1000; // mm
      const sizes = [15, 22, 28, 35, 42, 54];
      const pick = sizes.find((s) => COPPER[s].id >= dreq) || '>54';
      return [
        { label: 'Required bore', value: round(dreq, 1), unit: 'mm' },
        { label: 'Suggested copper size', value: pick, unit: 'mm' },
      ];
    },
  },
  {
    slug: 'pipe-volume',
    trade: 'plumbers',
    name: 'Pipe Volume (Water Content)',
    blurb: 'Water volume held in a length of copper pipe — handy for dosing inhibitor or sizing an expansion vessel.',
    inputs: [
      { key: 'size', label: 'Pipe size', type: 'select', default: '15',
        options: [15, 22, 28, 35, 42, 54].map((s) => ({ value: String(s), label: `${s}mm` })) },
      { key: 'length', label: 'Pipe length', unit: 'm', placeholder: '10' },
    ],
    compute: (v) => {
      const c = COPPER[Number(v.size)]; const len = N(v.length);
      if (len <= 0) return [{ label: 'Enter a length', value: 0, unit: '' }];
      const litres = (Math.PI / 4) * Math.pow(c.id / 1000, 2) * len * 1000;
      return [
        { label: 'Water content', value: round(litres, 2), unit: 'L' },
        { label: 'Per metre', value: round(litres / len, 3), unit: 'L/m' },
      ];
    },
  },
  {
    slug: 'pipe-weight',
    trade: 'plumbers',
    name: 'Pipe Weight',
    blurb: 'Weight of copper pipe empty and full of water — useful for fixing centres and handling.',
    inputs: [
      { key: 'size', label: 'Pipe size', type: 'select', default: '22',
        options: [15, 22, 28, 35, 42, 54].map((s) => ({ value: String(s), label: `${s}mm` })) },
      { key: 'length', label: 'Pipe length', unit: 'm', placeholder: '10' },
    ],
    compute: (v) => {
      const c = COPPER[Number(v.size)]; const len = N(v.length);
      if (len <= 0) return [{ label: 'Enter a length', value: 0, unit: '' }];
      const pipe = c.kg * len;
      const water = (Math.PI / 4) * Math.pow(c.id / 1000, 2) * len * 1000;
      return [
        { label: 'Pipe weight (empty)', value: round(pipe, 2), unit: 'kg' },
        { label: 'Water weight', value: round(water, 2), unit: 'kg' },
        { label: 'Total (full)', value: round(pipe + water, 2), unit: 'kg' },
      ];
    },
  },
  {
    slug: 'pipe-expansion',
    trade: 'plumbers',
    name: 'Pipe Thermal Expansion',
    blurb: 'How much a pipe expands with temperature — to allow for expansion loops and clip spacing.',
    inputs: [
      { key: 'length', label: 'Pipe length', unit: 'm', placeholder: '10' },
      { key: 'dt', label: 'Temperature rise', unit: '°C', placeholder: '50' },
      { key: 'material', label: 'Material', type: 'select', default: '0.0000165',
        options: [
          { value: '0.0000165', label: 'Copper (16.5 µm/m·°C)' },
          { value: '0.00015', label: 'Push-fit / PB plastic (150)' },
          { value: '0.00018', label: 'PEX (180)' },
          { value: '0.0002', label: 'MDPE (200)' },
        ] },
    ],
    compute: (v) => {
      const dl = N(v.length) * parseFloat(v.material) * N(v.dt) * 1000;
      if (N(v.length) <= 0) return [{ label: 'Enter length and temperature rise', value: 0, unit: '' }];
      return [{ label: 'Expansion', value: round(dl, 1), unit: 'mm' }];
    },
  },
  {
    slug: 'gas-pipe-sizing',
    trade: 'plumbers',
    name: 'Gas Pipe Sizing (BS 6891)',
    blurb: 'Minimum copper gas pipe size from appliance input and effective pipe length, to BS 6891 (1 mbar drop). Gas Safe work only — always verify on site.',
    inputs: [
      { key: 'kw', label: 'Appliance input (gross)', unit: 'kW', placeholder: '30' },
      { key: 'length', label: 'Effective pipe length (incl. fittings)', unit: 'm', placeholder: '6' },
    ],
    compute: (v) => {
      const kw = N(v.kw); const len = N(v.length);
      if (kw <= 0 || len <= 0) return [{ label: 'Enter input (kW) and length', value: 0, unit: '' }];
      const m3h = kw / 10.76; // gross CV ≈ 38.7 MJ/m³
      const size = [15, 22, 28, 35].find((s) => gasDischarge(s, len) >= m3h);
      return [
        { label: 'Gas rate', value: round(m3h, 2), unit: 'm³/h' },
        { label: 'Minimum copper pipe', value: size ? `${size}mm` : '> 35mm — see full tables', unit: '' },
        { label: 'Pipe discharge at this length', value: size ? gasDischarge(size, len) : 0, unit: 'm³/h' },
      ];
    },
  },
  {
    slug: 'gas-rate',
    trade: 'plumbers',
    name: 'Gas Rate Calculator',
    blurb: 'Work out appliance heat input from the gas meter. Time the volume used (e.g. 2 revolutions of the 0.01 m³ test dial = 0.02 m³).',
    inputs: [
      { key: 'volume', label: 'Gas volume used', unit: 'm³', placeholder: '0.02' },
      { key: 'time', label: 'Time taken', unit: 'seconds', placeholder: '40' },
      { key: 'cv', label: 'Calorific value', unit: 'MJ/m³', placeholder: '39.5', default: 39.5 },
    ],
    compute: (v) => {
      const vol = N(v.volume); const t = N(v.time);
      if (vol <= 0 || t <= 0) return [{ label: 'Enter volume and time', value: 0, unit: '' }];
      const rate = (vol / t) * 3600; // m³/h
      const gross = (rate * N(v.cv)) / 3.6; // kW
      return [
        { label: 'Gas rate', value: round(rate, 3), unit: 'm³/h' },
        { label: 'Heat input (gross)', value: round(gross, 1), unit: 'kW' },
        { label: 'Heat input (net ≈90%)', value: round(gross * 0.901, 1), unit: 'kW' },
      ];
    },
  },
  {
    slug: 'gas-pressure-drop',
    trade: 'plumbers',
    name: 'Gas Pressure Drop',
    blurb: 'Estimated pressure drop along a copper gas pipe for a given load, size and effective length (target ≤ 1 mbar meter-to-appliance).',
    inputs: [
      { key: 'kw', label: 'Appliance input (gross)', unit: 'kW', placeholder: '24' },
      { key: 'size', label: 'Pipe size', type: 'select', default: '22',
        options: [15, 22, 28, 35].map((s) => ({ value: String(s), label: `${s}mm` })) },
      { key: 'length', label: 'Effective pipe length', unit: 'm', placeholder: '8' },
    ],
    compute: (v) => {
      const kw = N(v.kw); const len = N(v.length); const size = Number(v.size);
      if (kw <= 0 || len <= 0) return [{ label: 'Enter input and length', value: 0, unit: '' }];
      const m3h = kw / 10.76;
      const cap = gasDischarge(size, len); // flow giving 1 mbar at this length
      const drop = cap > 0 ? Math.pow(m3h / cap, 1.82) : 99; // mbar
      return [
        { label: 'Gas rate', value: round(m3h, 2), unit: 'm³/h' },
        { label: 'Estimated pressure drop', value: round(drop, 2), unit: 'mbar' },
        { label: 'Within 1 mbar limit?', value: drop <= 1 ? '✓ OK' : '✗ Too high — upsize', unit: '' },
      ];
    },
  },
  {
    slug: 'flow-rate',
    trade: 'plumbers',
    name: 'Flow Rate (Bucket Test)',
    blurb: 'Convert a timed fill into a flow rate — fill a container of known volume and time it.',
    inputs: [
      { key: 'volume', label: 'Container volume', unit: 'L', placeholder: '10' },
      { key: 'time', label: 'Time to fill', unit: 'seconds', placeholder: '30' },
    ],
    compute: (v) => {
      const vol = N(v.volume); const t = N(v.time);
      if (vol <= 0 || t <= 0) return [{ label: 'Enter volume and time', value: 0, unit: '' }];
      return [
        { label: 'Flow rate', value: round((vol / t) * 60, 1), unit: 'L/min' },
        { label: 'Flow rate', value: round(vol / t, 2), unit: 'L/s' },
      ];
    },
  },
  {
    slug: 'pressure-loss',
    trade: 'plumbers',
    name: 'Pressure Loss (Head & Friction)',
    blurb: 'Estimated pressure at an outlet from the incoming pressure, the height it is lifted, and pipe friction. Estimate only.',
    inputs: [
      { key: 'inlet', label: 'Incoming pressure', unit: 'bar', placeholder: '3.0' },
      { key: 'height', label: 'Height risen', unit: 'm', placeholder: '6' },
      { key: 'run', label: 'Pipe run', unit: 'm', placeholder: '15', default: 0 },
      { key: 'friction', label: 'Friction allowance', unit: 'bar/m', placeholder: '0.02', default: 0.02 },
    ],
    compute: (v) => {
      const inlet = N(v.inlet);
      const headLoss = N(v.height) * 0.0981; // 1 m water ≈ 0.0981 bar
      const fric = N(v.run) * N(v.friction);
      const outlet = inlet - headLoss - fric;
      return [
        { label: 'Loss from height', value: round(headLoss, 2), unit: 'bar' },
        { label: 'Loss from friction', value: round(fric, 2), unit: 'bar' },
        { label: 'Pressure at outlet', value: round(outlet, 2), unit: 'bar' },
      ];
    },
  },
  {
    slug: 'cylinder-sizing',
    trade: 'plumbers',
    name: 'Hot Water Cylinder Sizing',
    blurb: 'Recommended hot water cylinder size by number of occupants and usage.',
    inputs: [
      { key: 'people', label: 'Occupants', unit: '', placeholder: '4' },
      { key: 'usage', label: 'Usage', type: 'select', default: '45',
        options: [{ value: '35', label: 'Low (35 L/person)' }, { value: '45', label: 'Average (45 L/person)' }, { value: '60', label: 'High / multiple bathrooms (60 L/person)' }] },
    ],
    compute: (v) => {
      const people = N(v.people);
      if (people <= 0) return [{ label: 'Enter occupants', value: 0, unit: '' }];
      const litres = people * parseFloat(v.usage);
      const std = [120, 150, 180, 210, 250, 300].find((s) => s >= litres) || '>300';
      return [
        { label: 'Estimated demand', value: round(litres, 0), unit: 'L' },
        { label: 'Suggested cylinder', value: std, unit: 'L' },
      ];
    },
  },
  {
    slug: 'tank-sizing',
    trade: 'plumbers',
    name: 'Cold Water Storage Sizing',
    blurb: 'Cold water storage cistern size — minimum 230 L per dwelling, or ~45 L per person where greater.',
    inputs: [
      { key: 'people', label: 'Occupants', unit: '', placeholder: '4' },
    ],
    compute: (v) => {
      const people = N(v.people);
      if (people <= 0) return [{ label: 'Enter occupants', value: 0, unit: '' }];
      const litres = Math.max(230, people * 45);
      const std = [230, 270, 320, 450, 680].find((s) => s >= litres) || '>680';
      return [
        { label: 'Required storage', value: round(litres, 0), unit: 'L' },
        { label: 'Suggested cistern', value: std, unit: 'L' },
      ];
    },
  },
  {
    slug: 'drain-fall',
    trade: 'plumbers',
    name: 'Drain Fall / Gradient',
    blurb: 'Total fall (drop) over a drain run for a chosen gradient. Typical foul: 110mm at 1:40–1:80; surface water 1:100–1:150.',
    inputs: [
      { key: 'length', label: 'Pipe run length', unit: 'm', placeholder: '10' },
      { key: 'gradient', label: 'Gradient (1 in …)', type: 'select', default: '80',
        options: [40, 60, 80, 100, 110, 150].map((g) => ({ value: String(g), label: `1 : ${g}` })) },
    ],
    compute: (v) => {
      const len = N(v.length); const g = parseFloat(v.gradient);
      if (len <= 0) return [{ label: 'Enter a pipe run length', value: 0, unit: '' }];
      const fall = (len * 1000) / g;
      return [
        { label: 'Total fall', value: round(fall, 0), unit: 'mm' },
        { label: 'Fall per metre', value: round(1000 / g, 1), unit: 'mm/m' },
        { label: 'Gradient', value: `1 : ${g}`, unit: '' },
      ];
    },
  },
  {
    slug: 'waste-pipe-sizing',
    trade: 'plumbers',
    name: 'Waste Pipe Sizing',
    blurb: 'Recommended waste pipe size, gradient and maximum run for common sanitary appliances (BS EN 12056 / Part H).',
    inputs: [
      { key: 'appliance', label: 'Appliance', type: 'select', default: 'sink',
        options: [
          { value: 'basin', label: 'Wash basin' }, { value: 'sink', label: 'Kitchen sink' },
          { value: 'bath', label: 'Bath' }, { value: 'shower', label: 'Shower' },
          { value: 'washing', label: 'Washing machine / dishwasher' }, { value: 'wc', label: 'WC (soil)' },
          { value: 'multi', label: 'Several appliances combined' },
        ] },
    ],
    compute: (v) => {
      const A = {
        basin: { d: 32, max: 1.7, grad: '1:40–1:110' }, sink: { d: 40, max: 3, grad: '1:40–1:110' },
        bath: { d: 40, max: 3, grad: '1:40–1:110' }, shower: { d: 40, max: 3, grad: '1:40–1:110' },
        washing: { d: 40, max: 3, grad: '1:40–1:110' }, wc: { d: 110, max: 6, grad: '1:40–1:80' },
        multi: { d: 50, max: 4, grad: '1:40–1:110' },
      };
      const a = A[v.appliance] || A.sink;
      return [
        { label: 'Waste pipe size', value: a.d, unit: 'mm' },
        { label: 'Max recommended run', value: a.max, unit: 'm' },
        { label: 'Gradient range', value: a.grad, unit: '' },
        { label: 'Trap seal depth', value: v.appliance === 'wc' ? 50 : 75, unit: 'mm' },
      ];
    },
  },
  {
    slug: 'rainwater-capacity',
    trade: 'plumbers',
    name: 'Rainwater / Gutter Sizing',
    blurb: 'Roof run-off flow and recommended gutter & downpipe size, based on a UK design rainfall of 75 mm/h.',
    inputs: [
      { key: 'area', label: 'Roof plan area drained', unit: 'm²', placeholder: '40' },
      { key: 'rainfall', label: 'Rainfall intensity', unit: 'mm/h', placeholder: '75', default: 75 },
    ],
    compute: (v) => {
      const area = N(v.area);
      if (area <= 0) return [{ label: 'Enter the roof area', value: 0, unit: '' }];
      const flow = (area * N(v.rainfall)) / 3600; // L/s  (1 mm/h over 1 m² = 1 L/h)
      const gutter = flow <= 1.1 ? '112mm half-round' : flow <= 1.7 ? '125mm half-round' : flow <= 2.6 ? '150mm half-round' : 'box / 170mm';
      const downpipe = flow <= 1.0 ? '68mm' : flow <= 1.7 ? '82mm' : '110mm';
      return [
        { label: 'Peak run-off', value: round(flow, 2), unit: 'L/s' },
        { label: 'Suggested gutter', value: gutter, unit: '' },
        { label: 'Suggested downpipe', value: downpipe, unit: '' },
      ];
    },
  },
  {
    slug: 'radiator-btu',
    trade: 'plumbers',
    name: 'Radiator BTU Sizing',
    blurb: 'Rough BTU heat-load by room volume + use factor.',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '4' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '3' },
      { key: 'height', label: 'Ceiling height', unit: 'm', placeholder: '2.4', default: 2.4 },
      { key: 'factor', label: 'Use factor (Bedroom 4, Living 5, Bath 6)', unit: 'x', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const vol = N(v.length) * N(v.width) * N(v.height);
      const btu = ceil(vol * 153 * (N(v.factor) / 5));
      return [
        { label: 'Room volume', value: round(vol, 2), unit: 'm³' },
        { label: 'BTU required', value: btu, unit: 'BTU/hr' },
        { label: 'Watts equivalent', value: ceil(btu * 0.293), unit: 'W' },
      ];
    },
  },
  {
    slug: 'boiler-size',
    trade: 'plumbers',
    name: 'Combi Boiler Sizing',
    blurb: 'Quick rule: 10kW + 2kW/rad + 3kW for HW (combi).',
    inputs: [
      { key: 'rads', label: 'Number of radiators', unit: '', placeholder: '8' },
      { key: 'baths', label: 'Bathrooms', unit: '', placeholder: '1', default: 1 },
    ],
    compute: (v) => {
      const kW = 10 + N(v.rads) * 2 + N(v.baths) * 3;
      return [
        { label: 'Recommended boiler', value: ceil(kW), unit: 'kW' },
      ];
    },
  },
  {
    slug: 'copper-pipe',
    trade: 'plumbers',
    name: 'Copper Pipe Quantity',
    blurb: 'Linear pipe + 10% fittings/cuts.',
    inputs: [
      { key: 'length', label: 'Total run', unit: 'm', placeholder: '20' },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const len = N(v.length) * (1 + N(v.wastage) / 100);
      return [
        { label: 'Pipe required', value: round(len, 2), unit: 'm' },
        { label: '3m lengths', value: ceil(len / 3), unit: 'pcs' },
      ];
    },
  },

];
